import React, { useCallback, useEffect, useState } from "react";
import api, { inr } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Edit2, X } from "lucide-react";

const empty = { title: "", category: "Surprise Gifts", image: "", weight: 10, estimated_value: 0, color: "#004BCA", active: true };

export default function AdminSpins() {
  const [prizes, setPrizes] = useState([]);
  const [winners, setWinners] = useState([]);
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("prizes");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const loadAll = useCallback(async () => {
    const [p, w, s] = await Promise.all([
      api.get("/admin/spin/prizes"),
      api.get("/admin/spin/winners"),
      api.get("/admin/spin/stats"),
    ]);
    setPrizes(p.data); setWinners(w.data); setStats(s.data);
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, weight: Number(form.weight), estimated_value: Number(form.estimated_value) };
    try {
      if (editing) await api.put(`/admin/spin/prizes/${editing}`, payload);
      else await api.post("/admin/spin/prizes", payload);
      toast.success("Saved");
      setForm(empty); setEditing(null);
      loadAll();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this prize?")) return;
    await api.delete(`/admin/spin/prizes/${id}`);
    loadAll();
  };

  const startEdit = (p) => { setForm(p); setEditing(p.prize_id); };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display text-3xl font-bold">Spin & Win Festival</h1>
        <div className="inline-flex bg-slate-100 rounded-full p-1">
          {["prizes","winners"].map(t => (
            <button key={t} data-testid={`tab-${t}`} onClick={()=>setTab(t)} className={`px-4 py-1.5 rounded-full text-sm font-medium capitalize ${tab===t?"bg-white shadow-sm":""}`}>{t}</button>
          ))}
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Stat label="Total Spins" value={stats.total_spins}/>
          <Stat label="Pending Payment" value={stats.pending_payment}/>
          <Stat label="Claimed" value={stats.claimed}/>
          <Stat label="Revenue" value={inr(stats.revenue)}/>
        </div>
      )}

      {tab === "prizes" ? (
        <div className="grid lg:grid-cols-[1fr_380px] gap-6">
          <div className="bg-white border border-slate-100 rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr><th className="text-left p-3">Prize</th><th>Category</th><th>Weight</th><th>Value</th><th>Active</th><th></th></tr>
              </thead>
              <tbody>
                {prizes.map(p => (
                  <tr key={p.prize_id} className="border-t border-slate-100">
                    <td className="p-3 flex items-center gap-3">
                      <div className="w-3 h-12 rounded-l" style={{background:p.color}}/>
                      <img src={p.image} className="w-10 h-12 rounded object-cover" alt=""/>
                      <span className="font-medium">{p.title}</span>
                    </td>
                    <td className="text-center text-xs">{p.category}</td>
                    <td className="text-center font-mono">{p.weight}</td>
                    <td className="text-center">{inr(p.estimated_value)}</td>
                    <td className="text-center">{p.active ? "✓" : "—"}</td>
                    <td className="text-right p-3 space-x-1">
                      <button data-testid={`edit-prize-${p.prize_id}`} onClick={()=>startEdit(p)} className="p-2 hover:bg-slate-100 rounded"><Edit2 size={14}/></button>
                      <button data-testid={`del-prize-${p.prize_id}`} onClick={()=>del(p.prize_id)} className="p-2 hover:bg-slate-100 rounded text-red-600"><Trash2 size={14}/></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <form onSubmit={save} className="bg-white border border-slate-100 rounded-2xl p-5 h-fit space-y-3">
            <div className="flex items-center justify-between">
              <div className="font-display font-bold inline-flex items-center gap-2"><Plus size={16}/> {editing?"Edit Prize":"Add Prize"}</div>
              {editing && <button type="button" onClick={()=>{setForm(empty);setEditing(null);}} className="text-xs text-slate-500 inline-flex items-center gap-1"><X size={12}/>Cancel edit</button>}
            </div>
            <Input label="Title" value={form.title} onChange={v=>setForm({...form,title:v})} testid="prz-title"/>
            <Input label="Category" value={form.category} onChange={v=>setForm({...form,category:v})} testid="prz-category"/>
            <Input label="Image URL" value={form.image} onChange={v=>setForm({...form,image:v})} testid="prz-image"/>
            <div className="grid grid-cols-2 gap-3">
              <Input label="Weight (1-100)" type="number" value={form.weight} onChange={v=>setForm({...form,weight:v})} testid="prz-weight"/>
              <Input label="Estimated Value (₹)" type="number" value={form.estimated_value} onChange={v=>setForm({...form,estimated_value:v})} testid="prz-value"/>
            </div>
            <label className="block">
              <span className="block text-xs font-semibold uppercase text-slate-500 mb-1">Wheel Slice Color</span>
              <input type="color" value={form.color} onChange={e=>setForm({...form,color:e.target.value})} className="w-full h-10 rounded-lg border border-slate-200" data-testid="prz-color"/>
            </label>
            <label className="flex items-center gap-2 bg-slate-50 rounded-xl px-3 py-2 cursor-pointer">
              <input type="checkbox" checked={form.active} onChange={e=>setForm({...form,active:e.target.checked})} data-testid="prz-active"/>
              <span className="text-sm font-medium">Active on wheel</span>
            </label>
            <button data-testid="prz-save" className="btn-primary-sf w-full py-2.5 rounded-full text-sm font-semibold">{editing?"Update Prize":"Add Prize"}</button>
          </form>
        </div>
      ) : (
        <div className="bg-white border border-slate-100 rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr><th className="text-left p-3">Winner</th><th>Prize</th><th>Won At</th><th>Claimed?</th><th>Order</th></tr>
            </thead>
            <tbody>
              {winners.length === 0 ? (
                <tr><td colSpan={5} className="p-8 text-center text-slate-500">No winners yet.</td></tr>
              ) : winners.map(w => (
                <tr key={w.spin_id} className="border-t border-slate-100">
                  <td className="p-3"><div className="font-medium">{w.user_name || "—"}</div><div className="text-xs text-slate-500">{w.user_email}</div></td>
                  <td className="text-center"><div className="font-medium">{w.prize?.title}</div><div className="text-xs text-slate-500">{w.prize?.category}</div></td>
                  <td className="text-center text-xs">{w.won_at?new Date(w.won_at).toLocaleString():"—"}</td>
                  <td className="text-center text-xs">{w.claimed ? <span className="text-emerald-600 font-semibold">Yes</span> : <span className="text-amber-600">Pending</span>}</td>
                  <td className="text-center text-xs font-mono">{w.order_id || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return <div className="bg-white border border-slate-100 rounded-2xl p-4"><div className="text-xs text-slate-500 uppercase tracking-wider">{label}</div><div className="font-display text-2xl font-bold mt-1">{value}</div></div>;
}
function Input({ label, type="text", value, onChange, testid }) {
  return <label className="block"><span className="block text-xs font-semibold uppercase text-slate-500 mb-1">{label}</span>
    <input data-testid={testid} type={type} value={value} onChange={e=>onChange(e.target.value)} required className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm"/></label>;
}
